import { createGrid, generateTerrain, worldToCell, type Grid } from "./grid";
import { PlantStore } from "./plant";
import { Rng } from "./rng";
import { step, createScratch, type SimScratch } from "./step";
import { sample, type MetricSample } from "./metrics";
import { SPECIES, SPECIES_IDS, speciesIndex, type SpeciesId } from "./species";

export interface WorldConfig {
  seed: number;
  gridSize: number;
  worldSize: number;
  waterLevel: number;
  capacity: number;
  /** World-seconds per tick. */
  dt: number;
}

export const DEFAULT_CONFIG: WorldConfig = {
  seed: 1,
  gridSize: 128,
  worldSize: 72, // larger island (≈2× the area of the original 50 m)
  waterLevel: 1.1,
  capacity: 8192,
  dt: 0.1,
};

/** How the harness "plants" an opening scene, standing in for the player. */
export interface Planting {
  species: SpeciesId;
  /** Number of clumps. */
  clumps: number;
  /** Individuals per clump. */
  per: number;
  /** Clump spread radius (m). */
  spread: number;
  /** Restrict clump centres to cells within this moisture band. */
  moisture?: [number, number];
}

// Sparse opening scene — mostly bare ground, so colonization is visible. Clump
// counts scale ~with island area, so density is preserved as the world grows.
export const DEFAULT_PLANTING: Planting[] = [
  { species: "grass", clumps: 16, per: 2, spread: 2.5 },
  { species: "heather", clumps: 6, per: 6, spread: 1.4, moisture: [0.2, 0.55] },
  { species: "reed", clumps: 6, per: 6, spread: 1.4, moisture: [0.75, 1.0] },
  { species: "rowan", clumps: 8, per: 1, spread: 0.0 },
];

/**
 * The pure, deterministic simulation world. No Babylon, no DOM. This is the
 * object the Phase 3 Worker will host unchanged.
 */
export class World {
  readonly config: WorldConfig;
  readonly grid: Grid;
  readonly plants: PlantStore;
  readonly rng: Rng;
  readonly scratch: SimScratch;
  readonly activeSeedCells = new Set<number>();
  time = 0;

  constructor(config: WorldConfig = DEFAULT_CONFIG) {
    this.config = config;
    this.grid = createGrid(config.gridSize, config.worldSize, config.waterLevel);
    generateTerrain(this.grid, config.seed);
    this.plants = new PlantStore(config.capacity);
    this.rng = new Rng(config.seed ^ 0x51ed);
    this.scratch = createScratch(this.grid, config.capacity);
  }

  /** Place an opening scene. Deterministic given the world seed. */
  plant(plan: Planting[] = DEFAULT_PLANTING): void {
    const { grid, rng, plants } = this;
    for (const p of plan) {
      const sp = SPECIES[p.species];
      const spIdx = speciesIndex(p.species);
      for (let c = 0; c < p.clumps; c++) {
        const center = this.findCell(p.moisture);
        if (!center) continue;
        for (let k = 0; k < p.per; k++) {
          const x = center.x + rng.gaussian() * p.spread;
          const z = center.z + rng.gaussian() * p.spread;
          const { gx, gz } = worldToCell(grid, x, z);
          if (grid.land[gz * grid.size + gx] === 0) continue;
          plants.spawn(spIdx, x, z, sp.seedlingB * 4, 0);
        }
      }
    }
  }

  /** Plant a single seedling of a species at a world point (player action). */
  plantOne(speciesIdx: number, x: number, z: number): boolean {
    const id = SPECIES_IDS[speciesIdx];
    if (!id) return false;
    const { gx, gz } = worldToCell(this.grid, x, z);
    if (this.grid.land[gz * this.grid.size + gx] === 0) return false;
    const sp = SPECIES[id];
    return this.plants.spawn(speciesIdx, x, z, sp.seedlingB * 4, 0) >= 0;
  }

  /** Reject-sample a land cell within an optional moisture band. */
  private findCell(moisture?: [number, number]): { x: number; z: number } | null {
    const { grid, rng } = this;
    const half = grid.worldSize * 0.5;
    for (let attempt = 0; attempt < 200; attempt++) {
      const x = rng.range(-half, half);
      const z = rng.range(-half, half);
      const { gx, gz } = worldToCell(grid, x, z);
      const i = gz * grid.size + gx;
      if (grid.land[i] === 0) continue;
      if (moisture) {
        const m = grid.moisture[i]!;
        if (m < moisture[0] || m > moisture[1]) continue;
      }
      return { x, z };
    }
    return null;
  }

  tick(): void {
    step({
      grid: this.grid,
      plants: this.plants,
      rng: this.rng,
      scratch: this.scratch,
      activeSeedCells: this.activeSeedCells,
      dt: this.config.dt,
    });
    this.time += this.config.dt;
  }

  sample(): MetricSample {
    return sample(this.plants, this.time);
  }
}

export { createGrid, generateTerrain, PlantStore, Rng, step, sample, SPECIES_IDS };
export type { Grid, MetricSample };
